import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye } from "lucide-react";
import { useLang } from "../../i18n";
import { resolveMedia } from "../../lib/api";
import { Reveal, SectionHeading } from "../Reveal";

const ABOUT_IMG = "/api/files/anansha-welfare/uploads/fcd53a0c-2782-4148-ba81-9299f7a5299b.jpeg";

export default function About() {
  const { t, pick } = useLang();
  return (
    <section data-testid="about-section" className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative order-2 lg:order-1">
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_24px_70px_rgba(23,107,58,0.18)]">
              <img
                src={resolveMedia(ABOUT_IMG)}
                alt="समुदाय के साथ संस्था की गतिविधि"
                className="w-full aspect-[4/5] object-cover hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ngo-forest/60 to-transparent" />
            </div>
            <div className="absolute -bottom-5 -right-3 sm:-right-5 bg-ngo-gold text-ngo-ink rounded-2xl px-6 py-4 shadow-lg">
              <p className="font-heading font-bold text-lg leading-tight">{pick({ hi: "स्थापना 2025", en: "Est. 2025" })}</p>
              <p className="text-xs font-medium opacity-80">VAR/07434/2024-2025</p>
            </div>
          </Reveal>

          <div className="order-1 lg:order-2">
            <SectionHeading
              overline={pick({ hi: "परिचय", en: "Introduction" })}
              title={t("nav.about")}
              testid="about-heading"
            />
            <Reveal i={2}>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-ngo-ink/75">
                {pick({
                  hi: "अंशा वेलफेयर सोसाइटी एक सामाजिक संस्था है, जिसका उद्देश्य समाज के जरूरतमंद वर्गों तक सहायता, जागरूकता और विकास के अवसर पहुंचाना है। संस्था शिक्षा, स्वास्थ्य, महिला सशक्तिकरण, बाल कल्याण, पर्यावरण संरक्षण, जल संरक्षण तथा सामुदायिक विकास जैसे क्षेत्रों में कार्य करने के लिए प्रतिबद्ध है।",
                  en: "Anansha Welfare Society is a social organization committed to bringing support, awareness and development opportunities to the needy sections of society across education, healthcare, women empowerment, child welfare, environment protection, water conservation and community development.",
                })}
              </p>
            </Reveal>
            <Reveal i={3}>
              <p className="mt-4 text-base md:text-lg leading-relaxed text-ngo-ink/75">
                {pick({
                  hi: "हमारा प्रयास केवल सहायता प्रदान करना नहीं, बल्कि लोगों को जागरूक, सक्षम और आत्मनिर्भर बनाने की दिशा में निरंतर कार्य करना है।",
                  en: "Our effort is not just to provide help, but to continuously work towards making people aware, capable and self-reliant.",
                })}
              </p>
            </Reveal>
            <Reveal i={4}>
              <Link
                to="/about"
                data-testid="about-more-btn"
                className="group mt-8 inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-ngo-deep text-white font-semibold hover:bg-ngo-forest hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
              >
                {pick({ hi: "हमारे बारे में और जानें", en: "Know More About Us" })}
                <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </Reveal>
          </div>
        </div>

        <div className="mt-24 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div data-testid="mission-card" className="relative h-full bg-ngo-forest text-ngo-cream rounded-3xl p-9 overflow-hidden">
              <span className="absolute -top-6 right-5 font-heading font-black text-[7rem] leading-none text-ngo-mint/10 select-none" aria-hidden="true">01</span>
              <div className="w-12 h-12 rounded-2xl bg-ngo-gold/20 flex items-center justify-center text-ngo-gold">
                <Target size={24} />
              </div>
              <h3 className="mt-6 font-heading font-bold text-2xl">{pick({ hi: "हमारा मिशन", en: "Our Mission" })}</h3>
              <p className="mt-4 leading-relaxed text-ngo-mint/80">
                {pick({
                  hi: "समाज के जरूरतमंद एवं वंचित वर्गों तक शिक्षा, स्वास्थ्य, जागरूकता, कौशल विकास और सामाजिक सहायता पहुंचाकर उन्हें बेहतर एवं सम्मानजनक जीवन के अवसर प्रदान करना।",
                  en: "To reach the needy and deprived sections of society with education, healthcare, awareness, skill development and social support, providing them opportunities for a better and dignified life.",
                })}
              </p>
            </div>
          </Reveal>
          <Reveal i={1}>
            <div data-testid="vision-card" className="relative h-full bg-ngo-mint border border-ngo-deep/10 rounded-3xl p-9 overflow-hidden">
              <span className="absolute -top-6 right-5 font-heading font-black text-[7rem] leading-none text-ngo-deep/10 select-none" aria-hidden="true">02</span>
              <div className="w-12 h-12 rounded-2xl bg-ngo-deep/10 flex items-center justify-center text-ngo-deep">
                <Eye size={24} />
              </div>
              <h3 className="mt-6 font-heading font-bold text-2xl text-ngo-ink">{pick({ hi: "हमारा विजन", en: "Our Vision" })}</h3>
              <p className="mt-4 leading-relaxed text-ngo-ink/70">
                {pick({
                  hi: "एक ऐसा समावेशी और आत्मनिर्भर समाज बनाना, जहां प्रत्येक व्यक्ति को शिक्षा, स्वास्थ्य, सम्मान, अवसर और विकास का समान अधिकार मिले।",
                  en: "To build an inclusive and self-reliant society where every individual has equal rights to education, health, dignity, opportunity and development.",
                })}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
